// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LazyImage, { LazyImageProps } from "@steiner/MobiusOverrides/LazyImage";
import parse from "html-react-parser";
import * as React from "react";

// XNG-Changes: adding fields
const enum fields {
    showCategoryImage = "showCategoryImage",
    showCategoryContent = "showCategoryContent",
}

interface OwnProps {
    fields: {
        [fields.showCategoryImage]: boolean;
        [fields.showCategoryContent]: boolean;
    };
}

type Props = OwnProps & WidgetProps & HasCategoryContext;

export interface CategoryDetailBannerStyles {
    container?: InjectableCss;
    // XNG-Changes
    image?: LazyImageProps;
    imageClickable?: ClickablePresentationProps;
}

export const bannerStyles: CategoryDetailBannerStyles = {};

const styles = bannerStyles;

const CategoryDetailBanner = ({ category, fields }: Props) => {
    // XNG-Changes
    const { showCategoryContent, showCategoryImage } = fields;

    // XNG-Changes: add new contitions
    if (
        !category ||
        (showCategoryContent && !category.htmlContent) ||
        (showCategoryImage && !category.largeImagePath)
    ) {
        return null;
    }

    /**
     * XNG-Changes
     * Change Structure
     * Add category largeImagePath
     */
    return (
        <StyledWrapper {...styles.container} data-test-selector="categoryDetailBanner_htmlContent">
            {showCategoryImage && (
                <Clickable {...styles.imageClickable} href={category.properties.categoryBannerLink}>
                    <LazyImage src={category.largeImagePath} altText={category.shortDescription} {...styles.image} />
                </Clickable>
            )}
            {showCategoryContent &&
                category.htmlContent &&
                parse(transformContentHtml(category.htmlContent), parserOptions)}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailBanner),
    definition: {
        group: "Category Details",
        icon: "image-landscape",
        allowedContexts: ["CategoryDetailsPage", "ProductListPage"],
        // XNG-Changes
        fieldDefinitions: [
            {
                name: fields.showCategoryContent,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showCategoryImage,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
